import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatNumber } from '../../utils/formatters.js';

export function ReceivePurchaseOrderModal({ po, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [receipts, setReceipts] = useState({}); // { lineId: quantityString }
  const [error, setError] = useState(null);

  const detail = useQuery({
    queryKey: ['inventory', 'purchase-orders', po?.id],
    queryFn: () => inventoryApi.getPurchaseOrder(po.id).then((r) => r.items),
    enabled: Boolean(po),
  });

  useEffect(() => {
    if (detail.data?.lines) {
      const initial = {};
      for (const line of detail.data.lines) {
        const outstanding = Number(line.quantity) - Number(line.receivedQuantity);
        initial[line.id] = outstanding > 0 ? outstanding.toString() : '0';
      }
      setReceipts(initial);
      setError(null);
    }
  }, [detail.data]);

  const receive = useMutation({
    mutationFn: ({ id, body }) => inventoryApi.receivePO(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'low-stock'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'movements'] });
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    const lines = (detail.data?.lines || [])
      .map((line) => {
        const qty = Number(receipts[line.id] || 0);
        return qty > 0 ? { lineId: line.id, quantity: qty } : null;
      })
      .filter(Boolean);
    if (lines.length === 0) {
      setError(t('inv.po.receive.intro'));
      return;
    }
    receive.mutate({ id: po.id, body: { lines } });
  }

  return (
    <Modal
      open={Boolean(po)}
      onClose={onClose}
      title={t('inv.po.receive.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={receive.isPending}>
            {receive.isPending ? `${t('common.save')}…` : t('inv.po.action.receive')}
          </Button>
        </>
      }
    >
      {po && (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <p className="muted text-sm" style={{ margin: 0 }}>
            <strong>{po.poNumber}</strong> - {t('inv.po.receive.intro')}
          </p>
          {(detail.data?.lines || []).map((line) => {
            const outstanding = Number(line.quantity) - Number(line.receivedQuantity);
            return (
              <div
                key={line.id}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
                  padding: 'var(--space-3)', border: '1px solid var(--color-hairline)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <strong>{line.sku} - {line.productName}</strong>
                <div className="muted text-xs" style={{ display: 'flex', gap: 'var(--space-3)' }}>
                  <span>{t('inv.po.receive.ordered')}: <span className="mono">{formatNumber(line.quantity)}</span></span>
                  <span>{t('inv.po.receive.alreadyReceived')}: <span className="mono">{formatNumber(line.receivedQuantity)}</span></span>
                  <span>{t('inv.po.receive.outstanding')}: <span className="mono">{formatNumber(outstanding)}</span></span>
                </div>
                <label className="field">
                  <span className="field-label">{t('inv.po.receive.thisReceipt')}</span>
                  <input
                    className="input"
                    type="number"
                    min="0"
                    max={outstanding}
                    step="any"
                    value={receipts[line.id] ?? '0'}
                    disabled={outstanding <= 0}
                    onChange={(e) => setReceipts((p) => ({ ...p, [line.id]: e.target.value }))}
                  />
                </label>
              </div>
            );
          })}
          {error && <div className="alert danger">{error}</div>}
        </form>
      )}
    </Modal>
  );
}
